import React, { useState, useEffect } from 'react';
import { listAuditLog } from '../../services/adminApi';
import { FileText, User } from 'lucide-react';

const ACTION_COLORS = {
  credits_added: 'bg-green-100 text-green-700',
  credits_removed: 'bg-red-100 text-red-700',
  premium_granted: 'bg-yellow-100 text-yellow-700',
  premium_revoked: 'bg-gray-100 text-gray-700',
  entitlement_granted: 'bg-blue-100 text-blue-700',
  entitlement_revoked: 'bg-gray-100 text-gray-700',
  package_created: 'bg-purple-100 text-purple-700',
  package_updated: 'bg-purple-100 text-purple-700',
  package_archived: 'bg-red-100 text-red-700',
  promo_code_created: 'bg-indigo-100 text-indigo-700',
  promo_code_updated: 'bg-indigo-100 text-indigo-700',
  promo_code_deleted: 'bg-gray-100 text-gray-700',
  promotion_created: 'bg-pink-100 text-pink-700',
  promotion_updated: 'bg-pink-100 text-pink-700',
  promotion_disabled: 'bg-gray-100 text-gray-700',
};

const AuditLogTab = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const res = await listAuditLog({ limit: 200 });
      setLogs(res.data.logs);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (d) => new Date(d).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' });

  const filtered = filter
    ? logs.filter(l => JSON.stringify(l).toLowerCase().includes(filter.toLowerCase()))
    : logs;

  if (loading) return <div className="p-8 text-center"><div className="animate-spin rounded-full h-10 w-10 border-4 border-[#009E60] border-t-transparent mx-auto"></div></div>;

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3">
        <FileText className="w-5 h-5 text-gray-500" />
        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Filter by admin, user, action..."
          className="flex-1 h-10 rounded-xl border border-gray-200 px-3 text-sm"
          data-testid="audit-filter"
        />
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-8 text-gray-500">No audit log entries</div>
      ) : (
        <div className="space-y-2" data-testid="audit-log-list">
          {filtered.map(log => (
            <div key={log.log_id} className="bg-white border border-gray-100 rounded-xl p-3">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${ACTION_COLORS[log.action] || 'bg-gray-100 text-gray-700'}`}>
                      {log.action.replace(/_/g, ' ')}
                    </span>
                    {log.target_user_id && (
                      <span className="text-xs text-gray-500 flex items-center gap-1">
                        <User className="w-3 h-3" /> {log.target_user_id.slice(-8)}
                      </span>
                    )}
                  </div>
                  {log.previous_value !== null && log.new_value !== null && (
                    <div className="text-sm text-gray-700 mt-1">
                      <span className="text-gray-500">Previous:</span>{' '}
                      <code className="text-xs bg-gray-100 px-1 rounded">{JSON.stringify(log.previous_value).slice(0, 60)}</code>
                      <span className="mx-2 text-gray-400">→</span>
                      <span className="text-gray-500">New:</span>{' '}
                      <code className="text-xs bg-gray-100 px-1 rounded">{JSON.stringify(log.new_value).slice(0, 60)}</code>
                    </div>
                  )}
                  {log.reason && (
                    <div className="text-xs text-gray-500 mt-1 italic">&ldquo;{log.reason}&rdquo;</div>
                  )}
                </div>
                <div className="text-xs text-gray-400 whitespace-nowrap">{formatDate(log.created_at)}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AuditLogTab;
